
import {
  useQuasar,
  useMeta,
  QVirtualScroll,
  QVirtualScrollProps,
} from 'quasar';
import {
  useRouter,
  useRoute,
  LocationQueryRaw,
  LocationQueryValueRaw,
} from 'vue-router';
import { ref, onMounted, watch } from 'vue';
import API, { MessageFilters } from 'boot/api';
import { matClose, matSearch, matRefresh } from '@quasar/extras/material-icons';
import { Message } from 'components/types';
import ChannelSelector from 'components/ChannelSelector.vue';
import ChatMessage from 'components/ChatMessage.vue';
import ParseMessage, { IRCMessage } from '../utils/ParseMessage';
import { stripActionMessage } from '../utils/MessageUtils';

function convertMessage(parsed: IRCMessage): Message {
  if (parsed.command === 'CLEARCHAT') {
    let message: string;

    // Parameter ban-duration is optional and contains the duration of the
    // timeout, in seconds. If ommited, the ban is permanent.
    if (parsed.tags['ban-duration']) {
      const seconds = Number(parsed.tags['ban-duration']).toLocaleString();
      message = `A moderator timed out ${parsed.trailing} for ${seconds} seconds.`;
    } else if (parsed.trailing) {
      message = `A moderator banned ${parsed.trailing}.`;
    } else {
      message = 'A moderator cleared chat for this room.';
    }

    return {
      uuid: parsed.tags['tmi-sent-ts'] + parsed.trailing,
      time: new Date(Number(parsed.tags['tmi-sent-ts'])),
      channel: parsed.params[0].slice(1),
      user: parsed.trailing,
      message,
    };
  }

  return {
    uuid: parsed.tags.id,
    time: new Date(Number(parsed.tags['tmi-sent-ts'])),
    channel: parsed.params[0].slice(1),
    user: parsed.tags['display-name'],
    message: stripActionMessage(parsed.trailing),
    userColor: parsed.tags.color,
    emotes: parsed.tags.emotes,
  };
}

export default {
  name: 'IndexPage',
  components: {
    ChannelSelector,
    ChatMessage,
  },
  setup() {
    const $q = useQuasar();
    const router = useRouter();
    const route = useRoute();
    let hasNextPage = false;
    let abortRequest: AbortController | undefined;

    const loading = ref(true);
    const virtualScroll = ref<QVirtualScroll>();
    const messageLoadLimit = 100;
    const messages = ref<readonly Message[]>([]);
    const messageSearch = ref<string|undefined>(
      route.query.message ? String(route.query.message) : undefined,
    );
    const userFilter = ref<string|undefined>(
      route.query.user ? String(route.query.user) : undefined,
    );
    const channelFilter = ref<string|undefined>(
      route.query.channel ? String(route.query.channel) : undefined,
    );

    useMeta({
      title: 'Twitch chat messages',
    });

    const onRequest = async (filters: MessageFilters, override = false) => {
      try {
        if (abortRequest) {
          // Cancel ongoing request.
          abortRequest.abort();
        }

        const { request, controller } = API.getMessages({
          limit: messageLoadLimit,
          user: userFilter.value,
          channel: channelFilter.value,
          message: messageSearch.value,
          ...filters,
        });

        abortRequest = controller;
        const data = await request;
        hasNextPage = data.length >= (filters.limit || messageLoadLimit);

        const parsedMessages: Message[] = [];
        for (let i = 0; i < data.length; i++) {
          const parsed = ParseMessage(data[i]);
          if (parsed) {
            parsedMessages.push(convertMessage(parsed));
          }
        }

        if (override) {
          messages.value = Object.freeze(parsedMessages);
        } else {
          messages.value = Object.freeze(messages.value.concat(parsedMessages));
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          // Ignore abort errors.
          return;
        }

        $q.notify({
          type: 'negative',
          message: String(err),
        });
      }
    };

    const onMessageScroll: QVirtualScrollProps['onVirtualScroll'] = async ({ to }) => {
      const messageCount = messages.value.length;
      const lastIndex = messageCount - 1;

      if (!loading.value && hasNextPage && messageCount > 0 && to === lastIndex) {
        const { time, uuid } = messages.value[lastIndex];
        await onRequest({
          continueTime: time.getTime(),
          continueUuid: uuid,
        });
      }
    };

    const onRefresh = async () => {
      loading.value = true;
      messages.value = Object.freeze([]);

      await onRequest({}, true);

      loading.value = false;
      virtualScroll.value?.refresh(0);
    };

    const setQueryParams = (initialKey: string, initialValue: LocationQueryValueRaw) => {
      const queryParams: LocationQueryRaw = {};
      const keys = ['message', 'user', 'channel'];

      keys.forEach((key) => {
        if (key !== initialKey && route.query[key]) {
          queryParams[key] = route.query[key];
        }
      });

      if (initialValue) {
        queryParams[initialKey] = initialValue;
      }

      return router.replace({ path: route.path, query: queryParams });
    };

    onMounted(async () => {
      await onRequest({}, true);
      loading.value = false;
    });

    watch(userFilter, async () => {
      await Promise.all([
        setQueryParams('user', userFilter.value),
        onRefresh(),
      ]);
    });
    watch(channelFilter, async () => {
      await Promise.all([
        setQueryParams('channel', channelFilter.value),
        onRefresh(),
      ]);
    });
    watch(messageSearch, async () => {
      await Promise.all([
        setQueryParams('message', messageSearch.value),
        onRefresh(),
      ]);
    });

    return {
      loading,
      virtualScroll,
      messages,
      messageSearch,
      userFilter,
      channelFilter,
      onMessageScroll,
      onRefresh,
      matClose,
      matSearch,
      matRefresh,
    };
  },
};
